import React from "react";
import * as Icon from "react-feather";
import { Layers } from "lucide-react";

import "./bulk-classify-modal.css";

import BookkeepingService from "../../../../services/BookkeepingService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import ToolsModal from "../kit/ToolsModal";
import ToolsSelect from "../kit/ToolsSelect";
import ToolsSummaryCard from "../kit/ToolsSummaryCard";

const KEEP_EXISTING_TAX_VALUE = "__keep__";

class BulkClassifyModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			classificationDraft: "",
			taxDraft: KEEP_EXISTING_TAX_VALUE,
		};
	}

	onApplyClicked = () => {
		const { onApply } = this.props;
		const { classificationDraft, taxDraft } = this.state;

		const keepsExistingTax = taxDraft === KEEP_EXISTING_TAX_VALUE;
		onApply({ classification: classificationDraft, tax: keepsExistingTax ? null : taxDraft });
	};

	countAlreadyClassified = () => {
		const { transactions } = this.props;
		const classifiedTransactions = transactions.filter(transaction => transaction.classification && transaction.classification !== "Unclassified");
		return classifiedTransactions.length;
	};

	renderSummary = () => {
		const { transactions } = this.props;

		const netAmount = transactions.reduce((runningTotal, transaction) => runningTotal + (Number(transaction.amount) || 0), 0);
		const roundedNetAmount = Math.round(netAmount * 100) / 100;
		const formattedNetAmount = UtilityService.formatCurrency(Math.abs(roundedNetAmount));
		const tone = roundedNetAmount < 0 ? "danger" : "success";
		const signPrefix = roundedNetAmount < 0 ? "-" : "";

		const bankAccountNames = transactions.map(transaction => transaction.bankAccountName || transaction.accountName || "");
		const namedBankAccounts = bankAccountNames.filter(Boolean);
		const uniqueBankAccounts = [...new Set(namedBankAccounts)];
		const bankAccountLabel = uniqueBankAccounts.length === 1 ? uniqueBankAccounts[0] : `${uniqueBankAccounts.length} bank accounts`;

		const transactionDates = transactions.map(transaction => transaction.date).filter(Boolean).sort();
		const earliestDate = transactionDates[0] || "";
		const latestDate = transactionDates[transactionDates.length - 1] || "";
		const dateLabel = earliestDate === latestDate ? earliestDate : `${earliestDate} — ${latestDate}`;

		const title = (
			<>
				<strong>{transactions.length} transactions</strong> selected
			</>
		);

		const dateMeta = dateLabel ? [{ icon: <Icon.Calendar size={13} />, text: dateLabel }] : [];
		const bankMeta = uniqueBankAccounts.length > 0 ? [{ icon: <Icon.CreditCard size={13} />, text: bankAccountLabel }] : [];

		return (
			<ToolsSummaryCard
				icon={<Layers size={20} />}
				tone={tone}
				title={title}
				value={`${signPrefix}${formattedNetAmount}`}
				valueSuffix="net"
				meta={[...dateMeta, ...bankMeta]}
			/>
		);
	};

	describeWhatWillChange = ({ anAccountWasChosen, aTaxWasChosen }) => {
		if (anAccountWasChosen && aTaxWasChosen) return "account and tax";
		if (anAccountWasChosen) return "account";
		return "tax";
	};

	renderFooter = () => {
		const { onClose, transactions } = this.props;
		const { classificationDraft, taxDraft } = this.state;

		const anAccountWasChosen = !!classificationDraft;
		const aTaxWasChosen = taxDraft !== KEEP_EXISTING_TAX_VALUE;
		const canApply = anAccountWasChosen || aTaxWasChosen;
		const whatWillChange = this.describeWhatWillChange({ anAccountWasChosen, aTaxWasChosen });

		return (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={onClose}>Cancel</button>
					<button type="button" className="tk-button tk-button--primary" onClick={this.onApplyClicked} disabled={!canApply}>
						<Icon.Check size={16} />
						Set the {whatWillChange} on {transactions.length} transactions
					</button>
				</span>
			</>
		);
	};

	render() {
		const { classifications, taxOptions, hasTax, onClose } = this.props;
		const { classificationDraft, taxDraft } = this.state;

		const classificationChoices = classifications.map(classification => ({
			value: classification.name,
			label: classification.name,
			hint: BookkeepingService.whichStatementAnAccountSitsOn({ statementType: classification.statement_type }),
		}));
		const taxChoices = taxOptions.map(tax => ({ value: tax, label: tax }));
		const taxSelectChoices = [
			{ value: KEEP_EXISTING_TAX_VALUE, label: "Keep each transaction's current tax" },
			{ value: "", label: "No tax" },
			...taxChoices,
		];

		const alreadyClassifiedCount = this.countAlreadyClassified();
		const willOverwriteClassifications = alreadyClassifiedCount > 0 && !!classificationDraft;

		return (
			<ToolsModal open={true} onClose={onClose} title="Classify selected transactions" width={560} context={this.renderSummary()} footer={this.renderFooter()}>
				<div className="tk-form-field">
					<div className="tk-form-label">Classification</div>
					<ToolsSelect
						value={classificationDraft}
						options={classificationChoices}
						onChange={selectedName => this.update({ classificationDraft: selectedName })}
						placeholder="Leave the accounts as they are…"
						searchable
						searchPlaceholder={`Search ${classifications.length} accounts…`}
					/>
					<div className="tk-form-help">Choose an account to record every selected transaction there, or leave this alone to change only the tax.</div>
				</div>

				{hasTax && (
					<div className="tk-form-field">
						<div className="tk-form-label">Tax treatment</div>
						<ToolsSelect
							value={taxDraft}
							options={taxSelectChoices}
							onChange={selectedTax => this.update({ taxDraft: selectedTax })}
						/>
						<div className="tk-form-help">Leave as-is to keep the tax already set on each transaction.</div>
					</div>
				)}

				{willOverwriteClassifications && (
					<div className="bk-bulk__overwrite-notice">
						<Icon.AlertTriangle size={14} className="bk-bulk__overwrite-icon" />
						<span>
							{alreadyClassifiedCount} of these {alreadyClassifiedCount === 1 ? "is" : "are"} already classified and will be changed.
						</span>
					</div>
				)}
			</ToolsModal>
		);
	}
}

export default BulkClassifyModal;
